import React from 'react'
import {
  ElementType,
  isFrameElement,
  isLayerElement,
  isLayerWithSharedStyle,
  SketchElement,
  SketchFrameElement,
} from '../../../../../../../inspector'
import * as S from './ElementInfo.styles'
import { ComponentName, SymbolPath } from '../../../components/ComponentName'
import { useDisplayedLayerNameAndOptionalPath } from '../useDisplayedLayerNameAndOptionalPath'
import { isPrivateCloud } from '@sketch/env-config'
import { SFSymbolLabel } from '@sketch/components'
import { ElementIcon } from '../../../../ElementIcon'

type ElementInfoProps = {
  sketchElement: SketchElement
  className?: string
}

export const ElementInfo = ({ sketchElement, className }: ElementInfoProps) => {
  const { displayedName, symbolNamePath } =
    useDisplayedLayerNameAndOptionalPath(sketchElement)

  if (!sketchElement) {
    return null
  }

  /**
   * We want to display as symbol the elements that are symbol instances
   * or the artboards that are symbol masters when the element is an artboard.
   */
  const isSymbol =
    sketchElement.type === ElementType.SymbolMaster ||
    sketchElement.type === ElementType.SymbolInstance

  const hasSharedStyle =
    (isFrameElement(sketchElement) || isLayerElement(sketchElement)) &&
    isLayerWithSharedStyle(sketchElement.style) &&
    Boolean(sketchElement.style.sharedStyleReference)

  const isComponent = isSymbol || hasSharedStyle

  const isLayerMasked = isLayerElement(sketchElement)
    ? sketchElement.isLayerMasked
    : false

  const frameElement: SketchFrameElement | undefined = isFrameElement(
    sketchElement
  )
    ? sketchElement
    : undefined

  /**
   * The symbol path dropdown is hidden in private cloud deployments since it
   * does not work for encrypted workspaces.
   * We plan on addressing this issue as part of a wider effort to fix "search"
   * features in encrypted workspaces: SWEB-1367
   */
  const showSymbolPath =
    !isPrivateCloud && symbolNamePath && symbolNamePath.length > 1

  return (
    <S.AttributeContainer className={className}>
      <S.ElementIconContainer $isComponent={isComponent}>
        <ElementIcon
          elementType={sketchElement.type}
          isLayerMasked={isLayerMasked}
          isComponent={isComponent}
          isStack={!!frameElement?.stack}
          stackDirection={frameElement?.stack?.direction}
          // Layers that are exportable gets an extra icon on top of the
          // normal layer type icon.
          isLayerExportable={sketchElement.isExportable}
        />
      </S.ElementIconContainer>
      <S.ComponentName data-testid="layer-name">
        <ComponentName isSymbol={isSymbol}>
          <SFSymbolLabel text={displayedName} />
        </ComponentName>
        {showSymbolPath && <SymbolPath paths={symbolNamePath} />}
      </S.ComponentName>
    </S.AttributeContainer>
  )
}
